import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { states, USER_LOAN_PROGRAM } from '@/config/constants';
import { useCreateProductMutation } from '@/apis/extended/productApi';
import { CommonDialog } from '../Common/CommonDialog';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';

type PropertyKind = 'purchase' | 'refinance' | 'investment';
type InvestmentTxn = 'purchase' | 'refinance';

/** @deprecated Use PropertyKind — kept for callers passing `initialMode="dscr"`. */
export type AddPropertyInitialMode = 'purchase' | 'refinance' | 'dscr';

interface AddPropertyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (productId: string) => void;
  initialMode?: AddPropertyInitialMode;
}

const initialAddress = { street: '', city: '', state: '', zip: '' };

function initialKindFromMode(
  mode?: AddPropertyInitialMode,
  presetRefi?: boolean
): PropertyKind {
  if (mode === 'dscr') return 'investment';
  if (mode === 'refinance' || presetRefi) return 'refinance';
  return 'purchase';
}

function resolveTransactionMode(
  kind: PropertyKind,
  investmentTxn: InvestmentTxn
): 'purchase' | 'refinance' {
  if (kind === 'refinance') return 'refinance';
  if (kind === 'investment') return investmentTxn;
  return 'purchase';
}

export const AddPropertyDialog = ({
  open,
  onClose,
  onCreated,
  initialMode,
}: AddPropertyDialogProps) => {
  const [searchParams] = useSearchParams();
  const presetRefi = searchParams.get('isRefinance') === '1';
  const [createProduct, { isLoading }] = useCreateProductMutation();
  const [kind, setKind] = useState<PropertyKind>(() =>
    initialKindFromMode(initialMode, presetRefi)
  );
  const [investmentTxn, setInvestmentTxn] = useState<InvestmentTxn>('purchase');
  const [price, setPrice] = useState('');
  const [address, setAddress] = useState({ ...initialAddress });
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isRefinanceContext =
    kind === 'refinance' ||
    (kind === 'investment' && investmentTxn === 'refinance');

  useEffect(() => {
    if (open) {
      setKind(initialKindFromMode(initialMode, presetRefi));
      setInvestmentTxn('purchase');
    }
  }, [open, initialMode, presetRefi]);

  const setAddressField =
    (field: keyof typeof initialAddress) => (value: string) => {
      setAddress((a) => ({ ...a, [field]: value }));
    };

  const isValid = Boolean(
    price && address.street && address.city && address.state && address.zip
  );

  const reset = () => {
    setName('');
    setPrice('');
    setAddress({ ...initialAddress });
    setInvestmentTxn('purchase');
    setSubmitted(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    setSubmitted(true);
    if (!isValid) return;
    const fullAddress = `${address.street}, ${address.city}, ${address.state} ${address.zip}`;
    const transactionMode = resolveTransactionMode(kind, investmentTxn);
    try {
      const created = await createProduct({
        name: name || fullAddress,
        price: parseFloat(price),
        transactionMode,
        ...(kind === 'investment'
          ? { savedLoanType: USER_LOAN_PROGRAM.DSCR }
          : {}),
        address: { ...address, fullAddress },
      }).unwrap();
      const id =
        (created as { _id?: string; id?: string })._id ??
        (created as { id?: string }).id;
      if (id) {
        toast.success('Property saved');
        reset();
        onCreated?.(id);
      }
    } catch (err) {
      toast.error('Could not save property');
      console.error(err);
    }
  };

  return (
    <CommonDialog
      isOpen={open}
      title="Add Property"
      icon="fa-light fa-house-circle-check"
      confirmButtonText="Save property"
      closeButtonText="Cancel"
      isLoading={isLoading}
      isConfirmDisabled={isLoading}
      onConfirm={handleCreate}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '640px',
          maxWidth: '92vw',
        },
      }}
    >
      <Stack gap={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            What kind of property is this?
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={kind}
            exclusive
            onChange={(_, v) => v && setKind(v as PropertyKind)}
            size="small"
          >
            <ToggleButton value="purchase">Purchase</ToggleButton>
            <ToggleButton value="refinance">Refinance</ToggleButton>
            <ToggleButton value="investment">Investment</ToggleButton>
          </ToggleButtonGroup>

          {kind === 'investment' ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
              >
                Are you buying or refinancing this investment?
              </Typography>
              <ToggleButtonGroup
                color="primary"
                value={investmentTxn}
                exclusive
                onChange={(_, v) => v && setInvestmentTxn(v as InvestmentTxn)}
                size="small"
              >
                <ToggleButton value="purchase">Purchase</ToggleButton>
                <ToggleButton value="refinance">Refinance</ToggleButton>
              </ToggleButtonGroup>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                Saved with investor / DSCR loan metrics on the next screen.
              </Typography>
            </Box>
          ) : null}
        </Box>

        <CommonFormControl
          label={isRefinanceContext ? 'Property value' : 'Listing price'}
          error={submitted && !price ? 'Required' : ''}
        >
          <CommonNumberInput
            value={price}
            placeholder="$"
            fullWidth
            onChange={(e) => setPrice(e.target.value)}
          />
        </CommonFormControl>

        <CommonFormControl
          label="Street"
          error={submitted && !address.street ? 'Required' : ''}
        >
          <TextField
            fullWidth
            value={address.street}
            onChange={(e) => setAddressField('street')(e.target.value)}
          />
        </CommonFormControl>

        <CommonFormControl
          label="City"
          error={submitted && !address.city ? 'Required' : ''}
        >
          <TextField
            fullWidth
            value={address.city}
            onChange={(e) => setAddressField('city')(e.target.value)}
          />
        </CommonFormControl>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CommonFormControl
              label="State"
              error={submitted && !address.state ? 'Required' : ''}
            >
              <Select
                value={address.state}
                onChange={(e: SelectChangeEvent) =>
                  setAddressField('state')(e.target.value)
                }
                fullWidth
                displayEmpty
              >
                <MenuItem value="">Select state</MenuItem>
                {states.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </CommonFormControl>
          </Grid>
          <Grid item xs={6}>
            <CommonFormControl
              label="ZIP"
              error={submitted && !address.zip ? 'Required' : ''}
            >
              <TextField
                fullWidth
                inputProps={{ maxLength: 5 }}
                value={address.zip}
                onChange={(e) => setAddressField('zip')(e.target.value)}
              />
            </CommonFormControl>
          </Grid>
        </Grid>

        <CommonFormControl label="Nickname (optional)">
          <TextField
            fullWidth
            placeholder="Defaults to the address"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </CommonFormControl>
      </Stack>
    </CommonDialog>
  );
};
